import debounce from "@/shared/utils/debounce";
import {
  Card,
  CardBody,
  CardHeader,
  InputSlots,
  Select,
  SelectItem,
  SelectSlots,
  SlotsToClasses,
  Textarea
} from "@nextui-org/react";
import { SquarePen } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from 'react-markdown';
import ComplianceAdherenceEdit from "./ComplianceAdherenceEdit";
import ComplianceAdherenceView from "./ComplianceAdherenceView";
import {
  OverviewCardPropsForCompliance,
  OverviewCardPropsForDropDown,
  OverviewCardPropsForDropDownAndTextArea,
  OverviewCardPropsForRiskAndJustification,
  OverviewCardPropsForTextArea,
  VendorRiskLevel
} from "./OverviewCards.types";
import RiskLevelChip from "./RiskLevelChip";
import { withControlTracking } from "./withControlTracking";

export const RiskAndJustitificationCard = withControlTracking(({
  onFocus,
  onBlur,
  onDropdownOpenChange,
  wrapSelectionChange,
  ...props
}: OverviewCardPropsForRiskAndJustification & {
  enhancedHeader: React.ReactNode,
  onFocus: () => void,
  onBlur: () => void,
  onDropdownOpenChange: (open: boolean) => void,
  wrapSelectionChange: (callback: (value: any) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => void
}) => {
  return (
    <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
      <CardHeader className="text-sm font-medium p-0 pb-2 flex justify-between">
        {props.enhancedHeader}
      </CardHeader>
      <CardBody className="p-0">
        <Select
          id={`${props.controlId}-select`}
          aria-label={props.title}
          placeholder="Select Option"
          selectedKeys={new Set([props.riskLevel])}
          onChange={wrapSelectionChange((value) => props.updateRiskLevel(value as VendorRiskLevel))}
          onOpenChange={onDropdownOpenChange}
          selectionMode={"single"}
          className="w-36 p-0 pb-4 bg-transparent bg-[#FAFAFA]"
          classNames={riskSelectFieldClassNames}
          renderValue={() => <RiskLevelChip riskLevel={props.riskLevel} />}
          isDisabled={props.isDisabled}
        >
          <SelectItem 
            key={"High"} 
            value={"High"} 
            textValue="High Risk"
            isDisabled={props.riskLevel === "High"}
          >
            <RiskLevelChip riskLevel="High" />
          </SelectItem>
          <SelectItem 
            key={"Medium"} 
            value={"Medium"} 
            textValue="Medium Risk"
            isDisabled={props.riskLevel === "Medium"}
          >
            <RiskLevelChip riskLevel="Medium" />
          </SelectItem>
          <SelectItem 
            key={"Low"} 
            value={"Low"} 
            textValue="Low Risk"
            isDisabled={props.riskLevel === "Low"}
          >
            <RiskLevelChip riskLevel="Low" />
          </SelectItem>
        </Select>

        <Textarea
          id={`${props.controlId}-textarea`}
          aria-label={props.title}
          value={props.textAreaValue}
          variant="flat"
          className="h-full shadow-none border-none disabled:cursor-default p-0 text-[#717179]"
          classNames={textAreaClassNames}
          style={{
            border: "0px",
            boxShadow: "unset",
            color: "#333333",
          }}
          onChange={(e) => props.handleTextAreaValueChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={props.isDisabled}
          placeholder="Add Justification"
        />
      </CardBody>
    </Card>
  );
});

export const TextAreaCard = withControlTracking(({ 
  enhancedHeader, 
  onFocus,
  onBlur,
  ...props 
}: OverviewCardPropsForTextArea & { 
  enhancedHeader: React.ReactNode,
  onFocus: () => void,
  onBlur: () => void
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const hasContent = props?.textAreaValue && typeof props.textAreaValue === 'string' && props.textAreaValue.trim() !== '';

  return (
    <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
      <CardHeader className="text-sm font-medium p-0 pb-2 flex justify-between">
        {enhancedHeader}
      </CardHeader>
      <CardBody className="p-0">
        {isEditMode || !hasContent ? (
          <Textarea
            id={props.controlId}
            aria-label={props.title}
            value={props.textAreaValue}
            variant="flat"
            className="h-full shadow-none border-none disabled:cursor-default p-0 text-[#717179]"
            classNames={textAreaClassNames}
            style={{
              border: "0px",
              boxShadow: "unset",
              color: "#333333",
            }}
            onChange={(e) => props.handleTextAreaValueChange(e.target.value)}
            onFocus={onFocus}
            onBlur={(e) => {
              onBlur();
              if (hasContent) {
                setIsEditMode(false);
              }
            }}
            disabled={props.isDisabled}
            placeholder={props.placeholder || `Add ${props.title}`}
            autoFocus={isEditMode}
          />
        ) : (
          <div 
            className="prose prose-sm max-w-none text-[#333333] cursor-pointer"
            onClick={() => {
              if (!props.isDisabled) {
                onFocus();
                setIsEditMode(true);
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !props.isDisabled) {
                onFocus();
                setIsEditMode(true);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-2 text-sm">{children}</p>,
                ul: ({ children }) => <ul className="list-disc ml-4 mb-2 text-sm">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal ml-4 mb-2 text-sm">{children}</ol>,
                li: ({ children }) => <li className="mb-1 text-sm">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-sm">{children}</strong>,
                em: ({ children }) => <em className="italic text-sm">{children}</em>,
                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
              }}
            >
              {props.textAreaValue}
            </ReactMarkdown>
          </div>
        )}
      </CardBody>
    </Card>
  );
});

export const DropDownAndTextAreaCard = withControlTracking(({
  onFocus,
  onBlur,
  onDropdownOpenChange,
  wrapSelectionChange,
  ...props
}: OverviewCardPropsForDropDownAndTextArea & {
  enhancedHeader: React.ReactNode,
  onFocus: () => void,
  onBlur: () => void,
  onDropdownOpenChange: (open: boolean) => void,
  wrapSelectionChange: (callback: (value: any) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => void
}) => {
  const [otherValue, setOtherValue] = useState(props.otherValues || []);

  useEffect(() => {
    setOtherValue(props.otherValues || []);
  }, [props.otherValues]);

  const debouncedHandleOtherValueChange = useMemo(
    () =>
      debounce((value: string) => {
        props.handleOtherValueChange?.([value]);
      }, 500),
    [props.handleOtherValueChange]
  );

  const handleOtherValueChange = (value: string) => {
    setOtherValue([value]);
    debouncedHandleOtherValueChange(value);
  };

  const handleDropDownChange = (values: string[]) => {
    if (!values.includes("Other")) {
      setOtherValue([]);
      props.handleOtherValueChange?.([]);
    }
    props.handleDropDownChanges(values);
  };

  return (
    <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
      <CardHeader className="text-sm font-medium p-0 pb-2">
        {props.enhancedHeader}
      </CardHeader>
      <CardBody className="p-0">
        <Select
          id={`${props.controlId}-select`}
          className="p-0 pb-4 border-none shadow-none"
          aria-label={props.title}
          placeholder="Select all applicable options"
          selectedKeys={new Set(props.dropdownValues)}
          onChange={wrapSelectionChange((value) => handleDropDownChange(value.split(",")))}
          onOpenChange={onDropdownOpenChange}
          selectionMode={props.selectionMode}
          variant="flat"
          isDisabled={props.isDisabled}
        >
          {props.dropDownOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </Select>

        {props.dropdownValues.includes("Other") && (
          <Textarea
            id={`${props.controlId}-other`}
            aria-label="Other data types"
            value={otherValue[0]}
            variant="flat"
            placeholder="Specify other data types"
            minRows={2}
            classNames={{
              inputWrapper:
                "w-full shadow-none rounded-md border-1 border-gray-300 bg-white mb-4",
            }}
            onChange={(e) => handleOtherValueChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={props.isDisabled}
          />
        )}

        <Textarea
          id={`${props.controlId}-textarea`}
          aria-label={props.title}
          value={props.textAreaValue}
          variant="flat"
          className="h-full shadow-none border-none disabled:cursor-default p-0 text-[#717179]"
          classNames={textAreaClassNames}
          style={{
            border: "0px",
            boxShadow: "unset",
            color: "#333333",
          }}
          onChange={(e) => props.handleTextAreaValueChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={props.isDisabled}
          placeholder="Add notes"
        />
      </CardBody>
    </Card>
  );
});

export const DropDownCard = withControlTracking(({
  onFocus,
  onBlur,
  onDropdownOpenChange,
  wrapSelectionChange,
  ...props
}: OverviewCardPropsForDropDown & { 
  enhancedHeader: React.ReactNode,
    onFocus: () => void,
    onBlur: () => void,
    onDropdownOpenChange: (open: boolean) => void,
    wrapSelectionChange: (callback: (value: any) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => void
}) => {
  return (
    <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
      <CardHeader className="text-sm font-medium p-0 pb-2">
        {props.enhancedHeader}
      </CardHeader>
      <CardBody className="p-1">
        <Select
          id={props.controlId}
          className="p-0 border-none shadow-none"
          aria-label={props.title}
          placeholder="Select Option"
          selectedKeys={new Set(props.dropdownValues)}
          onChange={wrapSelectionChange((value) => props.handleDropDownChanges(value.split(",")))}
          onOpenChange={onDropdownOpenChange}
          selectionMode={props.selectionMode}
          variant="flat"
          isDisabled={props.isDisabled}
        >
          {props.dropDownOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
      </CardBody>
    </Card>
  );
});

export const ComplianceAdherenceCard = withControlTracking(({
  ...props
}: OverviewCardPropsForCompliance & { enhancedHeader: React.ReactNode }) => {
  const [complianceAdherenceMode, setComplianceAdherenceMode] = useState<
    "VIEW" | "EDIT"
  >("VIEW");

  const parentRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // useEffect(() => {
  //   if (complianceAdherenceMode === 'EDIT'){

  //     const mouseClickHandler = (mouseEvent:MouseEvent) =>{
  //       if(parentRef.current){
  //         const verticalRange = [parentRef.current.offsetTop, parentRef.current.offsetTop + parentRef.current.clientHeight]
  //         const horizontalRange = [parentRef.current.offsetLeft, parentRef.current.offsetLeft + parentRef.current.clientWidth]
  //         if(!(verticalRange[0]<mouseEvent.clientY && verticalRange[1]>mouseEvent.clientY && horizontalRange[0]<mouseEvent.clientX && horizontalRange[1] > mouseEvent.clientX)){
  //           setComplianceAdherenceMode('VIEW')
  //         }
  //       }
  //     }

  //     window.addEventListener('click', mouseClickHandler)

  //     return () => {
  //       window.removeEventListener('click', mouseClickHandler)
  //     }
  //   }
  // },[complianceAdherenceMode])

  return (
    <div ref={parentRef} id="compliance-adherence-card">
      <Card className="w-full p-4 bg-[#FAFAFA] relative group shadow-none hover:shadow-small focus-within:shadow-small rounded-lg">
        <CardHeader className="text-sm font-medium p-0 pb-2">
          {props.enhancedHeader}
          {complianceAdherenceMode === "VIEW" &&
            (props.isDisabled ? null : (
              <button
                ref={buttonRef}
                onClick={() => setComplianceAdherenceMode("EDIT")}
                className="invisible group-hover:visible transition-all duration-300 ease-in-out cursor-pointer group-hover:cursor-pointer z-10 pointer-events-auto"
              >
                <SquarePen color="#006FEE" className="h-5 w-5" />
              </button>
            ))}
        </CardHeader>
        <CardBody className="p-0">
          {complianceAdherenceMode === "EDIT" ? (
            <ComplianceAdherenceEdit {...props} />
          ) : (
            <ComplianceAdherenceView {...props} />
          )}
        </CardBody>
      </Card>
    </div>
  );
});

const textAreaClassNames: SlotsToClasses<InputSlots> = {
  base:
    "shadow-none border-none bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  clearButton:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179] ",
  description:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  errorMessage:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  innerWrapper:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  input:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  helperWrapper:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  inputWrapper:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  label:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
  mainWrapper:
    "shadow-none border-none bg-[#FAFAFA] hover:bg-[#FAFAFA] disabled:cursor-default p-0 text-[#717179]",
};

const riskSelectFieldClassNames: SlotsToClasses<SelectSlots> = {
  base: "bg-[#FAFAFA]",
  mainWrapper: "bg-[#FAFAFA]",
  description: "bg-[#FAFAFA]",
  errorMessage: "bg-[#FAFAFA]",
  helperWrapper: "bg-[#FAFAFA]",
  innerWrapper: "bg-[#FAFAFA] h-6 min-h-6 hover:bg-[#FAFAFA] data-[hover=true]:bg-[#FAFAFA]",
  label: "bg-[#FAFAFA]",
  listbox: "bg-[#FAFAFA]",
  listboxWrapper: "bg-[#FAFAFA]",
  popoverContent: "bg-[#FAFAFA]",
  value: "bg-[#FAFAFA]",
  selectorIcon: "bg-transparent data-[hover=true]:bg-transparent",
  spinner: "bg-[#FAFAFA]",
  trigger: "bg-[#FAFAFA] shadow-none p-0 h-6 min-h-6 hover:bg-[#FAFAFA] data-[hover=true]:bg-[#FAFAFA]",
};
